import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Text } from "@fluentui/react";
import { Info } from "lucide-react";
import { getRatingEngineBranding } from "../providerBranding";
import type { EngineRendererProps } from "./RatingEnginesSection";
import { formatUsd, parseFiniteNumber } from "./rateDisplayFormat";

function truncateForCard(msg: string, maxLen = 120): string {
  const t = msg.replace(/\s+/g, " ").trim();

  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

/** Some persisted Sonar rows store TRAC under `trac.lanes[]` and scores under `laneScores.lanes[]` without `rates`. */
function firstNestedLane(
  row: Record<string, unknown>,
  key: "trac" | "laneScores"
): Record<string, unknown> | null {
  const block = row[key];

  if (!block || typeof block !== "object" || Array.isArray(block)) return null;
  const lanes = (block as Record<string, unknown>).lanes;
  const first = Array.isArray(lanes) ? lanes[0] : null;

  return first && typeof first === "object" && !Array.isArray(first)
    ? (first as Record<string, unknown>)
    : null;
}

function LaneScoreGauge({ score }: { score: number }) {
  const r = 32;
  const cx = 44;
  const cy = 44;
  const strokeWidth = 7;
  const circumference = Math.PI * r;
  const progress = Math.max(0, Math.min(100, score)) / 100;
  const dashOffset = circumference * (1 - progress);
  const trackPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
      <svg width="88" height="52" viewBox="0 0 88 52">
        <path
          d={trackPath}
          fill="none"
          stroke="#e1e1e1"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        <path
          d={trackPath}
          fill="none"
          stroke="#2563eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={`${dashOffset}`}
          style={{ transformOrigin: `${cx}px ${cy}px`, transform: "rotate(0deg)" }}
        />

        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          fontSize="18"
          fontWeight="700"
          fill="#111111"
          fontFamily="Arial, sans-serif"
        >
          {score}
        </text>
      </svg>
      <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
        <Text
          styles={{
            root: { fontSize: "10px", color: "#605e5c", textTransform: "uppercase" as const },
          }}
        >
          Lane Score
        </Text>
        <Info size={11} color="#605e5c" />
      </Box>
    </Box>
  );
}

export function SonarRateCard({ row, displayName, providerBranding }: EngineRendererProps) {
  const branding = getRatingEngineBranding(displayName, providerBranding);
  const rec = (row && typeof row === "object" && !Array.isArray(row) ? row : {}) as Record<
    string,
    unknown
  >;
  const rates = (rec.rates ?? {}) as Record<string, unknown>;
  const metadata = (row?.metadata ?? {}) as Record<string, unknown>;
  let low = parseFiniteNumber(rates.rate_low);
  let median = parseFiniteNumber(rates.rate) ?? parseFiniteNumber(rates.totalRate);
  let high = parseFiniteNumber(rates.rate_high);
  let laneScore = parseFiniteNumber(rates.lane_score);

  if (low == null && median == null && high == null) {
    const tracLane = firstNestedLane(rec, "trac");

    if (tracLane) {
      low = parseFiniteNumber(tracLane.rate_low);
      median = parseFiniteNumber(tracLane.rate) ?? parseFiniteNumber(tracLane.totalRate);
      high = parseFiniteNumber(tracLane.rate_high);
    }
  }
  if (laneScore == null) {
    const lsLane = firstNestedLane(rec, "laneScores");

    if (lsLane) laneScore = parseFiniteNumber(lsLane.lane_score);
  }

  const allRatesMissing = low == null && median == null && high == null;
  const rawFooter =
    typeof metadata.rateDetailError === "string" && metadata.rateDetailError.trim()
      ? metadata.rateDetailError.trim()
      : allRatesMissing && typeof metadata.error === "string" && metadata.error.trim()
        ? metadata.error.trim()
        : "";
  const footerText = rawFooter ? truncateForCard(rawFooter) : "";

  return (
    <Box sx={cardStyles.card}>
      {branding.logoSrc ? (
        <img
          src={branding.logoSrc}
          alt={branding.logoAlt}
          style={{
            height: `${branding.logoHeight}px`,
            width: "auto",
            objectFit: "contain",
            marginBottom: "10px",
            display: "block",
          }}
        />
      ) : (
        <Text styles={cardStyles.logoFallback}>{displayName}</Text>
      )}

      <Stack
        direction="row"
        sx={{ alignItems: "flex-end", gap: "0px", flexWrap: "wrap", rowGap: "8px" }}
      >
        <Stack direction="row" sx={{ flex: 1, minWidth: "200px", gap: "0px" }}>
          <PriceColumn label="Low (33%)" value={low} small />
          <PriceColumn label="Median" value={median} />
          <PriceColumn label="High (66%)" value={high} small />
        </Stack>
        {laneScore != null && (
          <Box sx={{ marginLeft: "8px", marginBottom: "2px" }}>
            <LaneScoreGauge score={laneScore} />
          </Box>
        )}
      </Stack>
      {footerText ? (
        <Text
          styles={{
            root: {
              display: "block",
              marginTop: "10px",
              fontSize: "11px",
              lineHeight: "15px",
              color: "#a4262c",
              fontWeight: 400,
            },
          }}
        >
          {footerText}
        </Text>
      ) : null}
    </Box>
  );
}

function PriceColumn({
  label,
  value,
  small,
}: {
  label: string;
  value: number | null;
  small?: boolean;
}) {
  return (
    <Box sx={{ minWidth: "72px", paddingRight: "8px" }}>
      <Text styles={cardStyles.priceLabel}>{label}</Text>
      <Text styles={small ? cardStyles.priceSmall : cardStyles.priceBold}>
        {value != null ? formatUsd(value) : "N/A"}
      </Text>
    </Box>
  );
}

const cardStyles = {
  card: {
    border: "1px solid #e1e1e1",
    borderRadius: "8px",
    padding: "12px 16px",
    backgroundColor: "#ffffff",
  },
  logoFallback: {
    root: {
      fontSize: "20px",
      fontWeight: "900",
      color: "#111111",
      letterSpacing: "2px",
      marginBottom: "10px",
      display: "block",
    },
  },
  priceLabel: {
    root: {
      fontSize: "10px",
      color: "#605e5c",
      display: "block",
      marginBottom: "2px",
    },
  },
  priceBold: {
    root: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#111111",
      display: "block",
    },
  },
  priceSmall: {
    root: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#323130",
      display: "block",
    },
  },
};
